import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useContent, resolveUrl } from "@/lib/content";
import { pageForPath, resolveSeo, SITE_URL, SITE_NAME } from "@/lib/seo";

const absolute = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : `${SITE_URL}${url}`;
};

// Renders title/meta/canonical/OG/JSON-LD for the current route. Blog posts override via <Seo>.
export const SeoManager = () => {
  const { pathname } = useLocation();
  const { content } = useContent();
  const page = pageForPath(pathname);
  if (!page || pathname.startsWith("/blog/")) return null;
  const seo = resolveSeo(page, content);
  return <Seo {...seo} isHome={page.key === "home"} />;
};

// Static defaults in index.html (marked data-static) serve non-JS social scrapers; drop them once Helmet renders page-specific tags.
const removeStaticDefaults = () => {
  document.head.querySelectorAll("[data-static]").forEach((el) => el.remove());
  const titles = document.head.querySelectorAll("title");
  titles.forEach((el, i) => { if (i > 0) el.remove(); });
};

export const Seo = ({ title, description, canonical, noindex = false, ogImage, siteName = SITE_NAME, twitter = "", googleVerification = "", type = "website", jsonLd, isHome = false }) => {
  const image = absolute(ogImage);
  useEffect(() => {
    const t = setTimeout(removeStaticDefaults, 50);
    return () => clearTimeout(t);
  }, [title]);
  const schemas = [
    isHome && {
      "@context": "https://schema.org", "@type": "Person", name: "Kalani Luana", url: SITE_URL,
      jobTitle: "Luxury Companion & Adult Film Actress", image: image || undefined,
      address: [{ "@type": "PostalAddress", addressLocality: "New York", addressRegion: "NY", addressCountry: "US" },
                { "@type": "PostalAddress", addressRegion: "CA", addressCountry: "US" }],
    },
    isHome && { "@context": "https://schema.org", "@type": "WebSite", name: siteName, url: SITE_URL },
    ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
  ].filter(Boolean);

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      {googleVerification && <meta name="google-site-verification" content={googleVerification} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      {twitter && <meta name="twitter:site" content={twitter.startsWith("@") ? twitter : `@${twitter}`} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};
